import _ from "lodash";

import { TColumnType } from "components/Table/model";
import { LOGOUT_REQUEST_SUCCESS } from "containers/authentication/redux/actionTypes";
import { IQuery } from "containers/lists/utils";
import { IAction } from "redux/store/model";

import { ISaveTableConfig } from "./../actionCreators/tableActionCreator";
import * as types from "./../actionTypes/tableActionTypes";
import initialState, {
  DEFAULT_DETECTION_CLASSES_CONFIG,
  DEFAULT_VARIANTS_CONFIG,
} from "./tableInitialState";

export type TTableConfig<T = any> = {
  path: string;
  columnTypes?: TColumnType<T>[];
  filters?: IQuery;
};

export interface ITableState {
  configs: TTableConfig[];
}

interface IRehydrateAction extends IAction {
  type: "persist/REHYDRATE";
  payload?: {
    table?: ITableState;
  };
}

function upsertTableConfig(
  configs: TTableConfig[],
  path: string,
  patch: Partial<TTableConfig>,
): TTableConfig[] {
  const newConfigs = _.map(configs, (item) =>
    item.path === path ? { ...item, ...patch } : item,
  );
  return _.some(configs, { path })
    ? newConfigs
    : [...newConfigs, { path, ...patch }];
}

export function addMissingDefaultConfigs(state: ITableState): ITableState {
  const configs = state.configs ?? [];
  const configsToAdd: TTableConfig[] = [];

  const hasDetectionClassesConfig = _.some(configs, {
    path: DEFAULT_DETECTION_CLASSES_CONFIG.path,
  });
  const hasVariantsConfig = _.some(configs, {
    path: DEFAULT_VARIANTS_CONFIG.path,
  });

  if (!hasDetectionClassesConfig) {
    configsToAdd.push(DEFAULT_DETECTION_CLASSES_CONFIG);
  }

  if (!hasVariantsConfig) {
    configsToAdd.push(DEFAULT_VARIANTS_CONFIG);
  }

  return {
    ...state,
    configs: [...configs, ...configsToAdd],
  };
}

export default function reducer(state = initialState, action: IAction) {
  switch (action.type) {
    // Saved filters belong to the session: the next user must not inherit them.
    case LOGOUT_REQUEST_SUCCESS:
      return initialState;

    case "persist/REHYDRATE": {
      const rehydrateAction = action as IRehydrateAction;
      const rehydratedState = rehydrateAction.payload?.table || state;
      return addMissingDefaultConfigs(rehydratedState);
    }

    case types.SET_TABLE_CONFIG: {
      const configs = state.configs ?? [];
      const { tableConfig } = action as ISaveTableConfig;
      return {
        ...state,
        configs: upsertTableConfig(configs, tableConfig.path, tableConfig),
      };
    }

    case types.CLEAN_TABLE_COLUMNS_ORDER: {
      return {
        ...state,
        columns: [],
      };
    }

    default:
      return state;
  }
}
